'use client';

import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ChangeEvent, MouseEvent } from 'react';
import { getPhoneDigits, parsePhoneInputToNational } from '@/core/utils/formatPhone';
import type { OnboardingFormProps } from '@/features/authentication/presentation/components/OnboardingForm';
import type { OnboardingPhoneVerifyStep } from '@/features/authentication/data/types/onboarding-form.types';
import { ONBOARDING_FORM_COPY, ONBOARDING_FORM_RESEND_COOLDOWN_SECONDS, ONBOARDING_FORM_RESEND_COOLDOWN_TICK_MS, ONBOARDING_LEGAL_ERROR_MISSING, ONBOARDING_PHONE_VERIFY_ERROR_SUBMIT_WITHOUT_SMS } from '../constants/onboarding.constants';
import { LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS, LOGIN_FORM_OTP_DIGIT_COUNT } from '../constants/login.constants';

export function useOnboardingForm({
  initialWhatsApp = '',
  initialEmail = '',
  initialName = '',
  phoneVerified = false,
  emailReadOnly = false,
  nameReadOnly = false,
  emailDraftHint = '',
  authLoading,
  submitLoading,
  onWhatsAppChange,
  onRequestSendPhoneCode,
  onResendPhoneCode,
  onConfirmLinkCode,
  onChangePhoneNumberStep,
  onSubmit,
  onValidationError,
  privacyModalBody,
  termsModalBody,
}: OnboardingFormProps) {
  const [whatsapp, setWhatsapp] = useState(initialWhatsApp);
  const [emailDraft, setEmailDraft] = useState(initialEmail);
  const [nameDraft, setNameDraft] = useState(initialName);
  const [companyDraft, setCompanyDraft] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  const isSubmittingRef = useRef(false);

  const [localPhoneVerified, setLocalPhoneVerified] = useState(false);
  const [phoneVerifyStep, setPhoneVerifyStep] = useState<OnboardingPhoneVerifyStep>('number');
  const [code, setCode] = useState('');
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [phoneSmsBusy, setPhoneSmsBusy] = useState(false);
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastAutoVerifyCodeRef = useRef<string | null>(null);

  const mustVerifyPhoneBySms = !phoneVerified;
  const phoneFieldLocked = phoneVerified || localPhoneVerified;

  const nameFilled = nameReadOnly ? (initialName?.trim() ?? '').length > 0 : nameDraft.trim().length > 0;
  const emailFilled = emailDraft.trim().length > 0;
  const companyFilled = companyDraft.trim().length > 0;
  const legalAccepted = acceptedTerms && acceptedPrivacy;
  const phoneOk = phoneVerified || localPhoneVerified;

  const canSubmitProfile =
    phoneOk &&
    emailFilled &&
    nameFilled &&
    companyFilled &&
    legalAccepted &&
    !authLoading;

  useEffect(() => {
    setWhatsapp(initialWhatsApp ?? '');
  }, [initialWhatsApp]);

  useEffect(() => {
    setNameDraft(initialName ?? '');
  }, [initialName]);

  useEffect(() => {
    const next = emailDraftHint.trim();
    if (!next) return;
    setEmailDraft((prev) => (prev.trim() === '' ? emailDraftHint : prev));
  }, [emailDraftHint]);

  useEffect(() => {
    if (phoneVerifyStep !== 'code') {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
      return;
    }
    if (resendCooldownSeconds <= 0) {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
      return;
    }
    if (cooldownIntervalRef.current) return;
    cooldownIntervalRef.current = setInterval(() => {
      setResendCooldownSeconds((prev) => {
        if (prev <= 1) {
          if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
            cooldownIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, ONBOARDING_FORM_RESEND_COOLDOWN_TICK_MS);
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
    };
  }, [phoneVerifyStep, resendCooldownSeconds]);

  useEffect(() => {
    if (!mustVerifyPhoneBySms || phoneFieldLocked) return;
    if (phoneVerifyStep !== 'code' || code.length !== LOGIN_FORM_OTP_DIGIT_COUNT) {
      if (code.length < LOGIN_FORM_OTP_DIGIT_COUNT) lastAutoVerifyCodeRef.current = null;
      return;
    }
    if (authLoading) return;
    if (lastAutoVerifyCodeRef.current === code) return;
    lastAutoVerifyCodeRef.current = code;

    void (async () => {
      const ok = await onConfirmLinkCode(code);
      if (ok) {
        setLocalPhoneVerified(true);
        setPhoneVerifyStep('number');
        setCode('');
        lastAutoVerifyCodeRef.current = null;
      } else {
        lastAutoVerifyCodeRef.current = code;
      }
    })();
  }, [
    mustVerifyPhoneBySms,
    phoneFieldLocked,
    phoneVerifyStep,
    code,
    authLoading,
    onConfirmLinkCode,
  ]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    if (!phoneOk) {
      onValidationError(ONBOARDING_PHONE_VERIFY_ERROR_SUBMIT_WITHOUT_SMS);
      return;
    }
    if (!legalAccepted) {
      onValidationError(ONBOARDING_LEGAL_ERROR_MISSING);
      return;
    }
    const name = nameReadOnly ? (initialName?.trim() ?? '') : nameDraft.trim();
    const companyName = companyDraft.trim();
    const emailTrimmed = emailDraft.trim();
    const email = emailTrimmed === '' ? null : emailTrimmed;
    const digits = getPhoneDigits(whatsapp);
    if (digits.length < LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS) {
      onValidationError(ONBOARDING_FORM_COPY.WHATSAPP_INVALID);
      return;
    }
    if (!companyFilled) {
      return;
    }
    if (!nameFilled) {
      return;
    }
    isSubmittingRef.current = true;
    void Promise.resolve(
      onSubmit({
        name,
        companyName,
        email,
        whatsappNationalDigits: digits,
        phoneVerified: phoneVerified || localPhoneVerified,
        acceptedTerms,
        acceptedPrivacy,
      })
    ).finally(() => {
      isSubmittingRef.current = false;
    });
  };

  const handleWhatsAppChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = parsePhoneInputToNational(e.target.value);
    setWhatsapp(v);
    onWhatsAppChange(v);
  };

  const handleSendPhoneVerification = async () => {
    const digits = getPhoneDigits(whatsapp);
    if (digits.length < LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS) return;
    setPhoneSmsBusy(true);
    try {
      const sent = await onRequestSendPhoneCode(digits);
      if (!sent) return;
      setPhoneVerifyStep('code');
      setCode('');
      setResendCooldownSeconds(ONBOARDING_FORM_RESEND_COOLDOWN_SECONDS);
    } finally {
      setPhoneSmsBusy(false);
    }
  };

  const handleResendCode = async (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    if (resendCooldownSeconds > 0 || authLoading || phoneSmsBusy) return;
    const digits = getPhoneDigits(whatsapp);
    if (digits.length < LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS) return;
    setPhoneSmsBusy(true);
    try {
      const sent = await onResendPhoneCode(digits);
      if (!sent) return;
      setCode('');
      setResendCooldownSeconds(ONBOARDING_FORM_RESEND_COOLDOWN_SECONDS);
    } finally {
      setPhoneSmsBusy(false);
    }
  };

  const handleChangeNumber = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setPhoneVerifyStep('number');
    setCode('');
    lastAutoVerifyCodeRef.current = null;
    onChangePhoneNumberStep();
  };

  const verifySendDisabled =
    getPhoneDigits(whatsapp).length < LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS ||
    authLoading ||
    phoneSmsBusy;

  return {
    copy: ONBOARDING_FORM_COPY,
    initialName,
    phoneVerified,
    emailReadOnly,
    nameReadOnly,
    authLoading,
    submitLoading,
    nameDraft,
    setNameDraft,
    companyDraft,
    setCompanyDraft,
    whatsapp,
    emailDraft,
    setEmailDraft,
    acceptedTerms,
    setAcceptedTerms,
    acceptedPrivacy,
    setAcceptedPrivacy,
    legalModal,
    setLegalModal,
    phoneFieldLocked,
    phoneVerifyStep,
    code,
    setCode,
    resendCooldownSeconds,
    canSubmitProfile,
    localPhoneVerified,
    handleSubmit,
    handleWhatsAppChange,
    handleSendPhoneVerification,
    handleResendCode,
    handleChangeNumber,
    privacyModalBody,
    termsModalBody,
    verifySendDisabled,
    phoneSmsBusy,
  };
}
